import { useCallback, useState } from 'react';
import ReactDOM from 'react-dom';

import Styles from './index.module.scss';

import { useClickOutside } from '@/app/hooks/useClickOutside';
import { Button } from '@/components/Elements/Button';
import { CloseIcon } from '@/components/Elements/Icons';



export type ChildrenMetaFunction<T> = (props: {
	meta: T | undefined;
	setMeta: React.Dispatch<React.SetStateAction<T | undefined>>;
}) => React.ReactNode;

interface Props<T> {
	title: string;
	header?: string;
	children?: React.ReactNode | ChildrenMetaFunction<T>;
	defaultMeta?: T;
	isLoading?: boolean;
	isDisabled?: boolean | ((meta: T | undefined) => boolean);
	onCloseRequest?: () => void;
	onClick?: (meta: T | undefined) => void;
	confirmText?: string;
	cancelText?: string;
	preventClose?: boolean;
	type?: HTMLButtonElement['type'];
	excluded?: HTMLElement[];
	variant?: 'delete' | 'warning';
}

export const DeleteModal = <T,>({
	title,
	children,
	isLoading,
	onClick,
	onCloseRequest,
	excluded,
	confirmText = 'Delete',
	cancelText = 'Cancel',
	preventClose = false,
	type = 'button',
	header = 'JUST CHECKING...',
	variant = 'delete',
	isDisabled = false,
	defaultMeta,
}: Props<T>) => {
	const [meta, setMeta] = useState<T | undefined>(defaultMeta);

	const close = useCallback(() => {
		if (preventClose) return;
		onCloseRequest?.();
	}, [preventClose, onCloseRequest]);

	const { ref } = useClickOutside<HTMLDivElement>(close, excluded);

	const isConfirmDisabled = typeof isDisabled === 'function' ? isDisabled(meta) : isDisabled;

	return (
		<>
			{ReactDOM.createPortal(
				<div className={Styles.container} ref={ref}>
					{!preventClose && (
						<button className={Styles.closeButton} type="button" onClick={close}>
							<CloseIcon />
						</button>
					)}
					<div className={Styles.header}>
						<h3>{header}</h3>
						<span>{title}</span>
					</div>
					<div className={Styles.body}>
						{typeof children === 'function' ? children({ meta, setMeta }) : children}
					</div>
					<div className={Styles.footer}>
						{variant === 'delete' && (
							<>
								<Button
									variant="secondary"
									type="button"
									className={Styles.cancel}
									disabled={isLoading}
									onClick={onCloseRequest}
								>
									{cancelText}
								</Button>
								<Button
									type={type}
									className={Styles.delete}
									disabled={isLoading || isConfirmDisabled}
									onClick={() => {
										onClick?.(meta);
									}}
								>
									{confirmText}
								</Button>
							</>
						)}
						{variant === 'warning' && (
							<Button
								type={type}
								className={Styles.delete}
								disabled={isLoading || isConfirmDisabled}
								onClick={close}
							>
								{confirmText}
							</Button>
						)}
					</div>
				</div>,
				document.body
			)}
		</>
	);
};
